import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { INTEGRATIONS, getIntegration } from '@/config/integrations';
import { useIntegrations } from '@/hooks/useIntegrations';

export type ClientAction =
  | { type: 'navigate_integrations'; integrationId?: string }
  | { type: 'open_connect_dialog'; integrationId: string }
  | { type: 'disconnect_personal'; integrationId: string }
  | { type: 'start_wrike_oauth' }
  | { type: 'list_integrations'; filter?: 'connected' | 'available' | 'all' };

export interface AgentResult {
  handled: boolean;
  reply?: string;
  actions?: ClientAction[];
}

interface ChatAgentCtx {
  /** Try to interpret a user chat message as integration intent. */
  tryHandle: (content: string) => Promise<{ handled: boolean; reply?: string }>;
}

const Ctx = createContext<ChatAgentCtx | null>(null);

export function IntegrationChatAgentProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const { connected, personal, disconnectPersonal } = useIntegrations();

  const buildCatalog = useCallback(
    () =>
      INTEGRATIONS.map((i) => ({
        id: i.id,
        name: i.name,
        category: i.category,
        scope: i.scope,
        connector: i.connector.kind,
        capabilities: i.capabilities,
        comingSoon: i.comingSoon,
        // Platform-configured services expose no management surface and no
        // secret identifiers to the model.
        platformManaged: i.platformManaged ?? false,
        secretName: undefined,
        helpUrl: i.connector.kind === 'apiKey' ? i.connector.helpUrl : undefined,
        connected: connected.has(i.id),
        personallyConnected: personal.has(i.id),
      })),
    [connected, personal]
  );

  const runAction = useCallback(
    async (action: ClientAction): Promise<string | null> => {
      switch (action.type) {
        case 'navigate_integrations': {
          const target = action.integrationId ? getIntegration(action.integrationId) : undefined;
          const search = target && !target.comingSoon && !target.platformManaged
            ? `?connect=${encodeURIComponent(target.id)}` : '';
          navigate(`/app/integrations${search}`);
          const i = action.integrationId ? getIntegration(action.integrationId) : null;
          return i
            ? `📂 Opened the Integrations page and surfaced **${i.name}** for you.`
            : `📂 Opened the Integrations page.`;
        }
        case 'open_connect_dialog': {
          const i = getIntegration(action.integrationId);
          if (!i || i.comingSoon || i.platformManaged) {
            navigate('/app/integrations');
            return i
              ? `ℹ️ **${i.name}** can't be connected yet — I opened the Integrations page instead.`
              : `⚠️ I couldn't find that integration in the catalog.`;
          }
          navigate(`/app/integrations?connect=${encodeURIComponent(i.id)}`);
          return `🪟 Opened **${i.name}**. Follow the steps shown there.`;
        }
        case 'disconnect_personal': {
          const i = getIntegration(action.integrationId);
          if (!i) return `⚠️ I couldn't find that integration in the catalog.`;
          if (!personal.has(action.integrationId)) {
            return `ℹ️ **${i.name}** has no saved label on your account, so there's nothing to remove.`;
          }
          try {
            await disconnectPersonal(action.integrationId);
            toast.success(`${i.name} label removed.`);
            return `✅ Removed the unverified **${i.name}** label from your account.`;
          } catch (err) {
            const msg = err instanceof Error ? err.message : 'Disconnect failed.';
            toast.error(`${i.name}: ${msg}`);
            return `❌ Couldn't unlink **${i.name}** — ${msg}`;
          }
        }
        case 'start_wrike_oauth': {
          try {
            const { data, error } = await supabase.functions.invoke('wrike-oauth-start', {
              body: { return_to: window.location.origin + '/app/integrations' },
            });
            if (error) throw error;
            const url = (data as { authorize_url?: string })?.authorize_url;
            if (!url) throw new Error('No authorize URL returned.');
            toast.success('Redirecting to Wrike…');
            window.location.href = url;
            return `🚀 Redirecting you to Wrike to authorize the connection…`;
          } catch (err) {
            const msg = err instanceof Error ? err.message : 'OAuth start failed.';
            toast.error(`Wrike: ${msg}`);
            return `❌ Couldn't start the Wrike sign-in — ${msg}\n\nDouble-check that Wrike credentials are configured, then try again.`;
          }
        }
        default: {
          // Unknown or removed actions (label-only connect, browser API-key
          // collection) are refused: they could never create a real connection.
          return `ℹ️ I can't do that from chat. I can open the Integrations page, where only currently supported setup options are offered.`;
        }
        case 'list_integrations': {
          const filter = action.filter ?? 'all';
          const list = INTEGRATIONS.filter((i) => {
            if (filter === 'connected') return connected.has(i.id);
            if (filter === 'available') return !i.comingSoon;
            return true;
          });
          if (list.length === 0) {
            return filter === 'connected'
              ? `ℹ️ No verified connections yet. Today Workitect supports the calendar subscription feed, Wrike migration, and attaching Google Drive links to tasks.`
              : `ℹ️ No integrations match that filter.`;
          }
          const lines = list
            .map((i) => `• ${i.name}${connected.has(i.id) ? ' ✓ connected' : ''}${i.comingSoon ? ' (coming soon)' : ''}`)
            .join('\n');
          const header =
            filter === 'connected'
              ? `✅ **Connected integrations** (${list.length})`
              : filter === 'available'
                ? `🧩 **Available integrations** (${list.length})`
                : `🧩 **Integrations catalog** (${list.length})`;
          return `${header}\n${lines}`;
        }
      }
    },
    [connected, disconnectPersonal, navigate, personal]
  );

  // Fast client-side prefilter — most chat messages have nothing to do with
  // integrations. Skip the paid AI round-trip entirely unless the message
  // mentions an integration keyword or a known integration name.
  const INTEGRATION_KEYWORDS = /\b(integration|integrations|connect|disconnect|reconnect|unlink|hook up|wire up|plug in|set up|oauth|api key|webhook)\b/i;
  const looksLikeIntegrationIntent = useCallback((content: string) => {
    if (INTEGRATION_KEYWORDS.test(content)) return true;
    const lower = content.toLowerCase();
    return INTEGRATIONS.some((i) => {
      const first = i.name.toLowerCase().split(' ')[0];
      return first.length >= 4 && lower.includes(first);
    });
  }, []);

  const tryHandle = useCallback(
    async (content: string) => {
      if (!looksLikeIntegrationIntent(content)) return { handled: false };
      let data: AgentResult | null = null;
      try {
        const res = await supabase.functions.invoke('integration-agent', {
          body: { content, catalog: buildCatalog() },
        });
        if (res.error) {
          console.warn('[integration-agent] invoke error', res.error);
          return { handled: false };
        }
        data = res.data as AgentResult;
      } catch (err) {
        console.warn('[integration-agent] failed', err);
        return { handled: false };
      }
      if (!data || !data.handled) return { handled: false };
      const extras: string[] = [];
      for (const action of data.actions ?? []) {
        try {
          const out = await runAction(action);
          if (out) extras.push(out);
        } catch (err) {
          const msg = err instanceof Error ? err.message : 'Unknown error';
          console.error('[integration-agent] action failed', action, err);
          extras.push(`❌ Something went wrong while running that action — ${msg}`);
        }
      }
      const reply = [data.reply, ...extras].filter(Boolean).join('\n\n');
      return { handled: true, reply: reply || `✅ Done.` };
    },
    [buildCatalog, looksLikeIntegrationIntent, runAction]
  );


  const value = useMemo(() => ({ tryHandle }), [tryHandle]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useIntegrationChatAgent(): ChatAgentCtx {
  const ctx = useContext(Ctx);
  if (!ctx) {
    // Provider not mounted (e.g. unauthenticated routes) — degrade quietly.
    return { tryHandle: async () => ({ handled: false }) };
  }
  return ctx;
}
